/**
 * Document Review: what the reader found in each client upload, for a person to confirm. The
 * document is on the left and what it is on the right; confirming files it in its year and folder,
 * ticks off the request it answers and keeps its figures for the return.
 */
import { DOC_OWNERS, READ_FLAGS, STANDARD_FOLDERS, TAX_DOC_TYPES, amountCents, isPermanentFolder, latestOpenYear, placementFor, taxDocType, yearChoices, type DocOwner, type ReadFlag } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Check, ChevronRight, FileSearch, RefreshCw, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { DocumentViewer } from "@/components/document-viewer";
import { Empty, Loading, PageHeader, Pager, Section, Segmented, useServerPaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/use-toast";
import { api, errorMessage, queryClient } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

type Status = "queued" | "reading" | "review" | "confirmed" | "dismissed";
type ListItem = {
  id: string;
  status: Status;
  source: "ai" | "manual";
  note: string;
  docType: string | null;
  taxYear: number | null;
  issuer: string | null;
  whose: DocOwner | null;
  flags: string[];
  uploadedByClient: boolean;
  createdAt: string;
  confirmedAt: string | null;
  clientId: string;
  clientName: string;
  document: { id: string; filename: string; contentType: string; folder: string };
  requestTitle: string | null;
};
type Detail = Omit<ListItem, "clientName" | "document" | "requestTitle"> & {
  fields: Record<string, string>;
  suggestedFolder: string | null;
  suggestedYear: number | null;
  requestId: string | null;
  retakeRequestId: string | null;
  recipientName: string | null;
  duplicateOfDocumentId: string | null;
  client: { id: string; name: string; kind: string };
  document: { id: string; filename: string; contentType: string; folder: string; year: number | null; createdAt: string; requestId: string | null };
  requests: Array<{ id: string; title: string; status: string }>;
  duplicateOf: { id: string; filename: string; createdAt: string } | null;
};

export const OWNER_LABELS: Record<DocOwner, string> = { taxpayer: "Taxpayer", spouse: "Spouse", joint: "Both spouses", dependent: "A dependent", business: "The business", unknown: "Not sure" };
const TABS: Array<[string, string]> = [
  ["review", "Waiting for review"],
  ["waiting", "Being read"],
  ["confirmed", "Confirmed"],
  ["dismissed", "Not needed"],
];

export function typeLabel(code: string | null) {
  if (!code) return "Not yet known";
  // The reader's "OTHER" means it could not tell what the document is; a bank statement says so.
  if (code === "OTHER") return "Not identified";
  if (code === "BANK_STATEMENT") return "Bank statement";
  return taxDocType(code).label;
}

function Flags({ flags }: { flags: string[] }) {
  if (!flags.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {flags.map((f) => (
        <span key={f} className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
          {READ_FLAGS[f as ReadFlag] ?? f}
        </span>
      ))}
    </span>
  );
}

export function DocumentReviewPage() {
  const search = new URLSearchParams(useSearch());
  const [, go] = useLocation();
  const id = search.get("id");
  const clientId = search.get("clientId") ?? undefined;
  const [tab, setTab] = useState(search.get("tab") ?? "review");
  const sp = useServerPaged(`${tab}|${clientId ?? ""}`);
  const query = `/api/document-reads?status=${tab}${clientId ? `&clientId=${clientId}` : ""}&offset=${sp.offset}&limit=${sp.size}`;
  const { data } = useQuery<{ items: ListItem[]; total: number; counts: Record<string, number> }>({ queryKey: [query], refetchInterval: tab === "waiting" ? 5_000 : 30_000, placeholderData: (prev, prevQuery) => (String(prevQuery?.queryKey[0]).includes(`status=${tab}&`) ? prev : undefined) });
  const items = data?.items ?? [];
  const open = (rid: string) => go(`/documents/review?id=${rid}${clientId ? `&clientId=${clientId}` : ""}&tab=${tab}`);
  if (id) {
    const next = items.findIndex((x) => x.id === id);
    const after = items[next + 1]?.id ?? (next === -1 ? items[0]?.id : undefined);
    return <ReviewOne key={id} id={id} back={() => go(`/documents/review?tab=${tab}${clientId ? `&clientId=${clientId}` : ""}`)} next={after && after !== id ? () => open(after) : undefined} />;
  }
  const c = data?.counts ?? {};
  return (
    <div>
      <PageHeader
        title="Document Review"
        description="What your clients uploaded, read automatically for clients who signed the AI consent. Check what was found and confirm: the document is filed in its year and folder, its request is ticked off, and its figures are kept for the return."
        crumbs={clientId && items[0] ? [{ label: "Clients", href: "/clients" }, { label: items[0].clientName, href: `/clients/${clientId}` }, { label: "Document Review" }] : undefined}
      />
      <Segmented
        label="Documents"
        className="mb-4"
        value={tab}
        onChange={setTab}
        options={TABS.map(([k, label]) => ({ value: k, label, count: k === "waiting" ? (c.queued ?? 0) + (c.reading ?? 0) : (c[k] ?? 0) }))}
      />
      {!data ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty>{tab === "review" ? "Nothing waiting. New client uploads appear here once they're read." : "Nothing here."}</Empty>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-card-border bg-card">
          {items.map((x) => (
            <li key={x.id}>
              <button onClick={() => open(x.id)} className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-muted/40">
                <FileSearch className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1 space-y-1">
                  <span className="block truncate text-sm font-medium">
                    {x.clientName} <span className="font-normal text-muted-foreground">· {x.document.filename}</span>
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {x.status === "queued" || x.status === "reading" ? "Being read…" : `${typeLabel(x.docType)}${x.taxYear ? ` · ${x.taxYear}` : ""}${x.issuer ? ` · ${x.issuer}` : ""}`}
                    {x.requestTitle ? ` · for "${x.requestTitle}"` : ""}
                  </span>
                  <span className="flex flex-wrap items-center gap-1.5">
                    {x.status === "review" && (
                      <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", x.source === "ai" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>
                        {x.source === "ai" ? "Read by AI" : "Enter by hand"}
                      </span>
                    )}
                    <Flags flags={x.flags} />
                  </span>
                </span>
                <span className="shrink-0 text-right text-xs text-muted-foreground">
                  {formatDate(x.createdAt)}
                  <ChevronRight className="ml-auto mt-1 h-4 w-4" aria-hidden />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {data && <Pager paged={sp.paged(data.total)} noun="documents" className="mt-3" />}
    </div>
  );
}

function ReviewOne({ id, back, next }: { id: string; back: () => void; next?: () => void }) {
  const { data } = useQuery<Detail>({ queryKey: ["/api/document-reads/", id], refetchInterval: (q) => (q.state.data && (q.state.data.status === "queued" || q.state.data.status === "reading") ? 4_000 : false) });
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={back}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> All documents
        </Button>
        {next && (
          <Button variant="outline" size="sm" onClick={next}>
            Next document <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>
      {!data ? (
        <Loading />
      ) : (
        <>
          <PageHeader
            title={data.document.filename}
            description={
              <>
                <Link href={`/clients/${data.client.id}`} className="text-primary hover:underline">
                  {data.client.name}
                </Link>{" "}
                · uploaded {formatDate(data.document.createdAt)}
                {data.uploadedByClient ? " by the client" : ""}
              </>
            }
          />
          <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="min-w-0 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-auto">
              <DocumentViewer doc={data.document} />
            </div>
            <div className="min-w-0">
              <ReviewForm data={data} onDone={next ?? back} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ReviewForm({ data, onDone }: { data: Detail; onDone: () => void }) {
  const reading = data.status === "queued" || data.status === "reading";
  const [docType, setDocType] = useState(data.docType ?? "OTHER");
  const [taxYear, setTaxYear] = useState(data.taxYear ? String(data.taxYear) : "");
  const [issuer, setIssuer] = useState(data.issuer ?? "");
  const [whose, setWhose] = useState<DocOwner>(data.whose ?? "unknown");
  const [fields, setFields] = useState<Record<string, string>>(data.fields);
  // Once confirmed, the document's own folder and year; before, where the reader suggests.
  const confirmed = data.status === "confirmed";
  const [folder, setFolder] = useState(confirmed ? data.document.folder : (data.suggestedFolder ?? data.document.folder));
  const [year, setYear] = useState(String((confirmed ? data.document.year : (data.suggestedYear ?? data.document.year)) ?? ""));
  const listed = (rid: string | null | undefined) => (rid && data.requests.some((q) => q.id === rid) ? rid : "");
  const [requestId, setRequestId] = useState(listed(data.requestId) || listed(data.document.requestId));
  // A request for several documents ("W-2s from every employer") stays open until staff say it's complete.
  const several = (rid: string) => /\b(every|all|each|any)\b|s from\b/i.test(data.requests.find((q) => q.id === rid)?.title ?? "");
  const [markReceived, setMarkReceived] = useState(!several(requestId));
  const type = taxDocType(docType);
  const today = new Date().toISOString().slice(0, 10);
  const years = useMemo(() => {
    const ys = yearChoices(today, 2015);
    return year && !ys.includes(Number(year)) ? [Number(year), ...ys].sort((a, b) => b - a) : ys;
  }, [today, year]);
  const folders = useMemo(() => Array.from(new Set([...STANDARD_FOLDERS, data.document.folder])), [data.document.folder]);

  // When staff change what the document is, its folder and year follow (until they choose them).
  const [placed, setPlaced] = useState(false);
  const first = useRef(`${docType}|${taxYear}`);
  useEffect(() => {
    if (placed || first.current === `${docType}|${taxYear}`) return;
    first.current = "";
    const p = placementFor(docType, Number(taxYear) || null);
    setFolder(p.folder === "General" ? data.document.folder : p.folder);
    if (p.year) setYear(String(Math.min(p.year, latestOpenYear(today))));
  }, [docType, taxYear, placed, data.document.folder, today]);

  const bad = type.fields.filter((f) => f.kind === "money" && fields[f.key] && amountCents(fields[f.key]) === null).map((f) => f.key);
  const done = (title: string) => {
    toast({ title });
    void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("/api/document-reads") || String(q.queryKey[0]).startsWith("/api/requests") });
    onDone();
  };
  const confirm = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send("POST", `/api/document-reads/${data.id}/confirm`, {
        docType,
        taxYear: taxYear ? Number(taxYear) : null,
        issuer: issuer.trim() || null,
        whose,
        fields: Object.fromEntries(type.fields.filter((f) => fields[f.key]?.trim()).map((f) => [f.key, fields[f.key]!.trim()])),
        folder,
        year: isPermanentFolder(folder) || !year ? null : Number(year),
        requestId: requestId || null,
        markReceived,
      }),
    onSuccess: () => done("Confirmed and filed"),
  });
  const dismiss = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/document-reads/${data.id}/dismiss`, {}), onSuccess: () => done("Marked as not needed") });
  const retake = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/document-reads/${data.id}/retake`, {}), onSuccess: () => done("The client has been asked for a clearer copy") });
  const reread = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", `/api/document-reads/${data.id}/reread`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["/api/document-reads/", data.id] }),
  });
  const error = confirm.error ?? dismiss.error ?? retake.error ?? reread.error;

  if (reading) {
    return (
      <Section title="Being read">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" aria-hidden /> Reading the document. This usually takes under a minute.
        </p>
      </Section>
    );
  }
  const locked = data.status === "dismissed";
  return (
    <div className="space-y-5">
      {(data.flags.length > 0 || data.note) && (
        <div className="space-y-1.5 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700/50 dark:bg-amber-950/30">
          {data.flags.map((f) => (
            <p key={f} className="flex items-start gap-1.5 font-medium">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden /> {READ_FLAGS[f as ReadFlag] ?? f}
              {f === "duplicate" && data.duplicateOf ? <span className="font-normal"> — "{data.duplicateOf.filename}", {formatDate(data.duplicateOf.createdAt)}</span> : null}
            </p>
          ))}
          {data.note && <p>{data.note}</p>}
          {data.retakeRequestId && <p className="text-xs text-muted-foreground">The client has been asked for a new copy.</p>}
        </div>
      )}
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {data.source === "ai" ? (
          <>
            <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden /> Read by AI: check each figure against the document before confirming.
          </>
        ) : (
          "Not read automatically: enter what the document is and its figures."
        )}
        {data.status === "confirmed" && " Confirmed; you can correct it and confirm again."}
      </p>

      <Section title="What it is">
        <fieldset disabled={locked} className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="r-type">Document</Label>
            <select id="r-type" value={docType} onChange={(e) => setDocType(e.target.value)} className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
              {TAX_DOC_TYPES.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="r-year">Tax year</Label>
            <Input id="r-year" inputMode="numeric" value={taxYear} onChange={(e) => setTaxYear(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="e.g. 2025" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="r-whose">Whose</Label>
            <select id="r-whose" value={whose} onChange={(e) => setWhose(e.target.value as DocOwner)} className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
              {DOC_OWNERS.map((o) => (
                <option key={o} value={o}>
                  {OWNER_LABELS[o]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="r-issuer">Issued by</Label>
            <Input id="r-issuer" value={issuer} maxLength={200} onChange={(e) => setIssuer(e.target.value)} placeholder="Employer, bank, lender, school or agency" />
            {data.recipientName && <p className="text-xs text-muted-foreground">Addressed to: {data.recipientName}</p>}
          </div>
        </fieldset>
      </Section>

      {type.fields.length > 0 && (
        <Section title="Figures" description="As printed on the document. Leave a box empty if it's blank.">
          <fieldset disabled={locked} className="grid gap-3">
            {type.fields.map((f) => {
              const fid = `r-f-${f.key}`;
              const wrong = bad.includes(f.key);
              return (
                <div key={f.key} className="grid min-w-0 items-center gap-1 sm:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] sm:gap-3">
                  <Label htmlFor={fid} className="text-xs font-normal text-muted-foreground">
                    {f.label}
                  </Label>
                  <div>
                    <Input
                      id={fid}
                      value={fields[f.key] ?? ""}
                      inputMode={f.kind === "money" ? "decimal" : undefined}
                      className={cn(f.kind === "money" && "tabular text-right")}
                      maxLength={200}
                      aria-invalid={wrong || undefined}
                      aria-describedby={wrong ? `${fid}-bad` : undefined}
                      onChange={(e) => setFields((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    />
                    {wrong && (
                      <p id={`${fid}-bad`} className="mt-0.5 text-xs text-destructive">
                        Enter an amount, such as 1,234.56
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </fieldset>
        </Section>
      )}

      <Section title="Filing">
        <fieldset disabled={locked} className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="r-folder">Folder</Label>
            <select id="r-folder" value={folder} onChange={(e) => (setPlaced(true), setFolder(e.target.value))} className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
              {folders.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="r-fyear">Year folder</Label>
            <select id="r-fyear" value={isPermanentFolder(folder) ? "" : year} disabled={isPermanentFolder(folder)} onChange={(e) => (setPlaced(true), setYear(e.target.value))} className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="">{isPermanentFolder(folder) ? "Permanent (no year)" : "By upload date"}</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            {type.yearly && taxYear && <p className="text-xs text-muted-foreground">A {taxYear} form goes in the {Number(taxYear) + 1} folder, the year its return is filed.</p>}
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="r-req">Answers the request</Label>
            <select id="r-req" value={requestId} onChange={(e) => (setRequestId(e.target.value), setMarkReceived(!several(e.target.value)))} className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="">None</option>
              {data.requests
                .filter((q) => q.status !== "accepted")
                .map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.title}
                  </option>
                ))}
              {data.requests.some((q) => q.status === "accepted") && (
                <optgroup label="Already received">
                  {data.requests
                    .filter((q) => q.status === "accepted")
                    .map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.title} (received)
                      </option>
                    ))}
                </optgroup>
              )}
            </select>
            {requestId && (
              <label className="flex items-center gap-2 pt-1 text-sm">
                <input type="checkbox" checked={markReceived} onChange={(e) => setMarkReceived(e.target.checked)} /> Mark it received (ticks it off the client's list)
              </label>
            )}
            {requestId && several(requestId) && !markReceived && (
              <p className="text-xs text-muted-foreground">This request asks for more than one document, so it stays on the client's list until you tick it.</p>
            )}
          </div>
        </fieldset>
      </Section>

      {error && <p className="text-sm text-destructive">{errorMessage(error)}</p>}
      <div className="flex flex-wrap gap-2">
        {!locked && (
          <Button disabled={confirm.isPending || bad.length > 0} onClick={() => confirm.mutate()}>
            <Check className="mr-1.5 h-4 w-4" /> {data.status === "confirmed" ? "Save changes" : "Confirm"}
          </Button>
        )}
        {data.status === "review" && !data.retakeRequestId && (
          <Button variant="outline" disabled={retake.isPending} onClick={() => retake.mutate()}>
            Ask for a clearer copy
          </Button>
        )}
        {data.status === "review" && data.source === "manual" && !data.flags.includes("duplicate") && (
          <Button variant="outline" disabled={reread.isPending} onClick={() => reread.mutate()}>
            <Sparkles className="mr-1.5 h-4 w-4" /> Read with AI again
          </Button>
        )}
        {data.status === "review" && (
          <Button variant="ghost" disabled={dismiss.isPending} onClick={() => dismiss.mutate()}>
            <X className="mr-1.5 h-4 w-4" /> Not needed
          </Button>
        )}
      </div>
    </div>
  );
}
